import { Chip, DateSpan, FileItem, Metadata, useFileUrl, useNumberFormat, useTranslations } from "@harea/component"
import { TMaternityLeave, TTimeOff } from "@harea/component"

type Props = Partial<TMaternityLeave> & { leave?: TTimeOff }

export const MaternityDetails = (props: Props) => {
  const [
    PROBABLE_DELIVERY_DATE,
    PREGNANCY_CERTIFICATE,
    LEAVE_REQUEST,
    MATERNITY_BOOKLET,
    MEDICAL_PROOFS,
    MATERNITY_STATUS,
    MATERNITY_LEAVE,
    ESTIMATE_TITLE,
    ESTIMATE_UNAVAILABLE,
    GROSS_MONTHLY_SALARY,
    EMPLOYER_RATE,
    VALIDATION_REQUIRED,
    MONTH,
    LEAVE_PERIOD,
    DAYS,
    EMPLOYER_PART,
    CNAPS_PART,
    ESTIMATE_NOTE,
  ] = useTranslations(i18n)

  const fileUrl = useFileUrl()
  const formatCurrency = useNumberFormat({ style: "currency", currency: "MGA" })
  const estimate = props.leave?.maternitySalaryEstimate

  const status = props.status
  const statusColor = status === "APPROVED" ? "success" : status === "REJECTED" ? "danger" : "info"
  const statusLabel = status === "APPROVED" ? "std_approved" : status === "REJECTED" ? "std_refused" : "std_pending"

  const renderFile = (label: string, file?: any) => {
    if (!file?.id) {
      return (
        <Metadata
          label={label}
          className="grid grid-cols-[160px_1fr] gap-4 items-center"
          value={<span className="text-muted">-</span>}
        />
      )
    }

    return (
      <Metadata
        label={label}
        className="grid grid-cols-[160px_1fr] gap-4 items-center"
        value={
          <a href={fileUrl(file.id)} target="_blank" rel="noreferrer">
            <FileItem {...file} />
          </a>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <h3>{MATERNITY_LEAVE}</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {props.probableDeliveryDate ? (
          <Metadata
            label={PROBABLE_DELIVERY_DATE}
            className="grid grid-cols-[160px_1fr] gap-4 items-center"
            value={<DateSpan value={props.probableDeliveryDate} format="LL" />}
          />
        ) : (
          <Metadata
            label={PROBABLE_DELIVERY_DATE}
            className="grid grid-cols-[160px_1fr] gap-4 items-center"
            value={<span className="text-muted">-</span>}
          />
        )}

        {status ? (
          <Metadata
            label={MATERNITY_STATUS}
            className="grid grid-cols-[160px_1fr] gap-4 items-center"
            value={<Chip className={`chip-${statusColor}`} label={statusLabel} />}
          />
        ) : (
          <Metadata
            label={MATERNITY_STATUS}
            className="grid grid-cols-[160px_1fr] gap-4 items-center"
            value={<span className="text-muted">-</span>}
          />
        )}
      </div>

      <div className="flex flex-col gap-2">
        {renderFile(PREGNANCY_CERTIFICATE, props.pregnancyCertificate)}
        {renderFile(LEAVE_REQUEST, props.leaveRequest)}
        {renderFile(MATERNITY_BOOKLET, props.maternityBooklet)}
        {renderFile(MEDICAL_PROOFS, props.medicalProofs)}
      </div>
      {props.leave?.type?.designation === "MATERNAL" && (
        <div className="flex flex-col gap-3">
          <h3>{ESTIMATE_TITLE}</h3>
          {!estimate ? (
            <p className="text-muted">{ESTIMATE_UNAVAILABLE}</p>
          ) : (
            <>
              <p className="text-sm text-muted">{props.employee?.fullName} — {GROSS_MONTHLY_SALARY} : {formatCurrency(estimate.salary)} ; {EMPLOYER_RATE} : {estimate.employerRate} %.</p>
              {!props.leave?.isValidated && <p className="text-sm text-muted">{VALIDATION_REQUIRED}</p>}
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead><tr><th>{MONTH}</th><th>{LEAVE_PERIOD}</th><th>{DAYS}</th><th>{EMPLOYER_PART}</th><th>{CNAPS_PART}</th></tr></thead>
                  <tbody>{estimate.periods.map((period) => <tr key={period.month}><td>{period.month}</td><td><DateSpan value={period.dateBegin} format="DD/MM/YYYY" /> – <DateSpan value={period.dateEnd} format="DD/MM/YYYY" /></td><td>{period.days}</td><td>{formatCurrency(period.employer)}</td><td>{formatCurrency(period.cnaps)}</td></tr>)}</tbody>
                </table>
              </div>
              <p className="text-sm text-muted">{ESTIMATE_NOTE}</p>
            </>
          )}
        </div>
      )}
    </div>
  )
}

const i18n = [
  "std_probable_delivery_date",
  "std_pregnancy_certificate",
  "std_leave_request",
  "std_maternity_booklet",
  "std_medical_proofs",
  "std_status",
  "std_maternity_leave",
  "std_maternity_salary_estimate",
  "std_maternity_estimate_unavailable",
  "std_maternity_gross_monthly_salary",
  "std_employer_rate",
  "std_maternity_validation_required",
  "std_month",
  "std_maternity_leave_period",
  "std_days",
  "std_employer_part",
  "std_maternity_cnaps_part",
  "std_maternity_estimate_note",
  "std_approved",
  "std_refused",
  "std_pending",
]
